import { toast } from "sonner";
import { ApiResponse } from "../types/responseType";
import customFetch from "../util/config/axiosConfig";
import {
  Admission,
  CreateAdmissionPayload,
  MoveAdmissionPayload,
} from "../types/admissionType";
import { PaginatedResponse } from "../types/paginationType";
import { getErrorMessage } from "../util/functions/UniversalFunctions";

const admissionApis = {
  // GET all
  getAdmissions: async (params: {
    page?: number;
    size?: number;
    studentName?: string;
    motherName?: string;
    fatherName?: string;
    dateOfBirth?: string;
    academicCycle?: string;
    branchCode: string;
  }): Promise<ApiResponse<PaginatedResponse<Admission>>> => {
    try {
      const { page = 0, size = 10, ...filters } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await customFetch.get<
        ApiResponse<PaginatedResponse<Admission>>
      >(`/api/v1/admission?${queryParams.toString()}`);

      return response.data;
    } catch (error: any) {
      console.error("Error fetching admissions:", error);
    }
  },

  // POST
  createAdmission: async (
    payload: CreateAdmissionPayload,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.post<ApiResponse<string>>(
        "/api/v1/admission",
        payload,
      );

      toast.success(response.data.data || "Admission added successfully");
      return response.data;
    } catch (error: any) {
      toast.error(getErrorMessage(error) || "Failed to create admission");
      console.error("Error creating admission:", error);
    }
  },

  // GET BY ID
  getAdmissionById: async (
    id: number,
  ): Promise<ApiResponse<Admission> | undefined> => {
    try {
      const response = await customFetch.get<ApiResponse<Admission>>(
        `/api/v1/admission/${id}`,
      );
      return response.data;
    } catch (error: any) {
      toast.error("Error fetching admission by id:", error);
    }
  },

  // Update Admission
  updateAdmission: async (
    id: number,
    payload: CreateAdmissionPayload,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.put<ApiResponse<string>>(
        `/api/v1/admission/${id}`,
        payload,
      );

      toast.success("Admission updated successfully");
      return response.data;
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to update admission");
    }
  },

  // Move admission
  moveAdmissionToClass: async ({
    admissionId,
    status,
    classId,
  }: MoveAdmissionPayload): Promise<ApiResponse<string>> => {
    try {
      const response = await customFetch.get<ApiResponse<string>>(
        `/api/v1/admission/move-to/${admissionId}`,
        {
          params: { status, classId },
        },
      );

      toast.success(response.data.data || "Admission updated successfully");
      return response.data;
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error || "Failed to update admission status",
      );
    }
  },

  // DELETE
  deleteAdmission: async (
    id: number,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.delete<ApiResponse<string>>(
        `/api/v1/admission/delete/${id}`,
      );

      toast.success(response.data.data || "Admission deleted successfully");
      return response.data;
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to delete admission");
      console.error("Error deleting admission:", error);
    }
  },
};

export default admissionApis;
